// =============================================================================
// LIVE AUDIO — Phase 3/4: viewer-side audio-only playback for the Control Room
// =============================================================================
// When the host Control Room sets mode='audio' (Slideshow), viewers play the browser
// mixer's stereo stream (app/mhv-audio) instead of taking audio from the OME video, and
// the black video player is hidden (nothing is published to mhv-live in that mode).
//
// PER-BUS MUTE: the host now broadcasts a STEREO-SPLIT mix — Voice (A+B) on the LEFT
// channel, Music on the RIGHT channel. This viewer splits them back apart, runs each
// through its own gain, and re-merges BOTH buses into BOTH output channels, so a normal
// listener hears voice+music centered while the Voice / Music pills can silence one bus
// locally (per-listener, not for everyone). The audible output is a real <audio> element
// fed by the Web Audio graph (not ac.destination) so a playing media element gives the
// best chance of surviving a phone lock/background. If the split can't be built (very old
// browser), the media element carries the raw stream as an audible fallback (no per-bus).
//
// UNIFIED MASTER MUTE: there is ONE master mute button/icon on the live page
// (#mute-toggle-btn, owned by live-player.js). In audio mode that button is routed here
// (window.MHVLiveAudio.toggleMute), and a small drop-up caret (#mute-caret-btn) opens the
// Voice/Music pills (#mute-bus-popover) — both only visible in audio mode (body.mhv-audio-mode).
//
// Self-contained: injects a tiny style + an offscreen audio mount; needs OvenPlayer
// (already on the live page) + window.socket. Marker: MHV_LIVE_AUDIO_5.
// (Renamed 4→5 alongside the SVG mute-icon swap to escape a wedged Shopify CDN
// snapshot and stay coupled to host-control-room-15.js.)
(function () {
  'use strict';

  var STREAM = 'https://stream.graniteunionauction.com/app/mhv-audio';
  // Shared mute icon (same asset the video player uses) so ONE button/icon works in both modes.
  var ICON_MUTED = 'https://cdn.shopify.com/s/files/1/0073/9101/9067/files/mute.png?v=1754371518';
  var ICON_ON = 'https://cdn.shopify.com/s/files/1/0073/9101/9067/files/audio-on.png?v=1754371513';
  var mode = 'video', connected = false, player = null, retryT = null, audioEl = null;
  var ac = null, srcNode = null, splitter = null, voiceGain = null, musicGain = null, merger = null;
  var sinkDest = null, sinkEl = null, hookT = null;
  var masterOn = true, voiceOn = true, musicOn = true, gestureWired = false, mediaSessionSet = false, pillsWired = false;

  // ---- UI (over the SCREEN, not the chat) ----------------------------------
  // Slideshow/audio mode: hide the video's Play button (no video to play) AND hide the
  // black video player itself (nothing is published to mhv-live in this mode). The single
  // mute button (#mute-toggle-btn) stays visible and is reused for the audio stream.
  function injectStyle() {
    if (document.getElementById('mhv-audio-style')) return;
    var css =
      'body.mhv-audio-mode #play-pause-btn{display:none!important}' +
      'body.mhv-audio-mode #whep-player-container{display:none!important}' +
      // OvenPlayer overwrites the mount's inline styles when it builds the audio-only
      // player, so it pops a black (no video track) box into the page flow at the bottom
      // of <body> — over the chat on mobile. Pin it 1px + offscreen with !important
      // (beats OvenPlayer's inline styles) while keeping it rendered so iOS keeps the audio.
      '#mhv-audio-mount{position:fixed!important;left:-9999px!important;top:0!important;bottom:auto!important;right:auto!important;width:1px!important;height:1px!important;max-width:1px!important;max-height:1px!important;min-width:0!important;min-height:0!important;overflow:hidden!important;opacity:0!important;pointer-events:none!important;z-index:-1!important}' +
      '#mhv-audio-mount video,#mhv-audio-mount .op-wrapper,#mhv-audio-mount .ovenplayer{width:1px!important;height:1px!important;min-width:0!important;min-height:0!important}';
    var s = document.createElement('style'); s.id = 'mhv-audio-style'; s.textContent = css;
    document.head.appendChild(s);
  }
  function ensureUI() {
    injectStyle();
    if (!document.getElementById('mhv-audio-mount')) {
      var mount = document.createElement('div'); mount.id = 'mhv-audio-mount';
      mount.style.cssText = 'position:absolute;left:-9999px;width:2px;height:2px;overflow:hidden';
      document.body.appendChild(mount);
    }
  }
  var graphReady = function () { return !!(voiceGain && musicGain && sinkEl); };
  // Is the audio stream currently audible (playing, unlocked, master + at least one bus on)?
  function isAudible() {
    if (!player || !masterOn) return false;
    if (graphReady()) return (voiceOn || musicOn) && (!ac || ac.state === 'running') && !sinkEl.paused;
    return !!(audioEl && !audioEl.paused);   // pre-graph / fallback: element carries audio
  }
  // Reflect audio state on the shared mute icon (truthful: muted icon until unlocked/on).
  function paint() {
    if (mode !== 'audio') return;
    var on = isAudible();
    // PNG icon kept in sync (hidden in audio mode by CSS, but restored on exit to video).
    var icon = document.getElementById('mute-icon');
    if (icon) { icon.src = on ? ICON_ON : ICON_MUTED; icon.alt = on ? 'Mute' : 'Unmute'; }
    // Inline SVG speaker is what actually shows in audio mode: .off = muted (X), else waves.
    var svg = document.getElementById('mute-icon-svg');
    if (svg) svg.classList.toggle('off', !on);
    var btn = document.getElementById('mute-toggle-btn');
    if (btn) btn.setAttribute('aria-label', on ? 'Mute' : 'Unmute');
  }
  // Reflect Voice/Music pill state (muted class + aria) — independent of master.
  function paintPills() {
    var pv = document.getElementById('mute-pill-voice');
    var pm = document.getElementById('mute-pill-music');
    if (pv) { pv.classList.toggle('muted', !voiceOn); pv.setAttribute('aria-checked', voiceOn ? 'true' : 'false'); }
    if (pm) { pm.classList.toggle('muted', !musicOn); pm.setAttribute('aria-checked', musicOn ? 'true' : 'false'); }
  }

  // The MASTER mute button, when in audio mode. First tap turns audio ON; after that it
  // toggles everything. Per-bus preferences (Voice/Music) are preserved across a master mute.
  function toggleMute() {
    var wasAudible = isAudible();
    enableAudio();
    if (wasAudible) {
      masterOn = false;                       // mute all (keep the pill choices for later)
    } else {
      masterOn = true;
      if (!voiceOn && !musicOn) { voiceOn = true; musicOn = true; }  // nothing would play → restore both
    }
    applyOutput(); paint(); paintPills();
  }
  // A single Voice or Music pill (per-listener). Turning a bus on implies you want audio,
  // so it also lifts a master mute.
  function toggleBus(which) {
    enableAudio();
    if (which === 'music') musicOn = !musicOn; else voiceOn = !voiceOn;
    if ((voiceOn || musicOn) && !masterOn) masterOn = true;
    applyOutput(); paint(); paintPills();
  }

  // ---- Output routing ------------------------------------------------------
  function setGains(vG, mG) { if (voiceGain) voiceGain.gain.value = vG; if (musicGain) musicGain.gain.value = mG; }
  function applyOutput() {
    if (!player) return;
    if (graphReady()) {
      // Web Audio graph is the audible, re-centered path — keep the element silent.
      setMute(true);
      setGains(masterOn && voiceOn ? 1 : 0, masterOn && musicOn ? 1 : 0);
      if (sinkEl.paused) { try { sinkEl.play(); } catch (e) {} }
    } else {
      // Pre-graph / split unavailable: the media element carries the (uncentered) stream.
      // Audible-but-lopsided beats silent; per-bus mute is unavailable until the graph builds.
      setMute(!masterOn);
    }
    if ('mediaSession' in navigator) navigator.mediaSession.playbackState = isAudible() ? 'playing' : 'paused';
  }
  function setMute(m) { try { player.setMute(m); } catch (e) { if (audioEl) audioEl.muted = m; } }
  function enableAudio() {
    if (ac && ac.state !== 'running') ac.resume();
    if (player && player.play) { try { player.play(); } catch (e) {} }
    if (audioEl && audioEl.paused) { try { audioEl.play(); } catch (e) {} }
    if (sinkEl && sinkEl.paused) { try { sinkEl.play(); } catch (e) {} }
    applyOutput(); paint();
  }

  function buildGraph(v) {
    if (ac) return;
    try {
      ac = new (window.AudioContext || window.webkitAudioContext)();
      srcNode = ac.createMediaStreamSource(v.srcObject);
      splitter = ac.createChannelSplitter(2);
      voiceGain = ac.createGain(); musicGain = ac.createGain();
      merger = ac.createChannelMerger(2);
      srcNode.connect(splitter);
      // Host stereo-split: LEFT = Voice bus, RIGHT = Music bus. Route EACH bus into BOTH
      // output channels so both are centered, while each gain independently mutes its bus.
      splitter.connect(voiceGain, 0);
      splitter.connect(musicGain, 1);
      voiceGain.connect(merger, 0, 0); voiceGain.connect(merger, 0, 1);
      musicGain.connect(merger, 0, 0); musicGain.connect(merger, 0, 1);
      // Audible sink = a real <audio> element playing the processed MediaStream (not
      // ac.destination) so a playing media element keeps the audio session alive when the
      // phone locks/backgrounds — same pattern the host monitor uses.
      sinkDest = ac.createMediaStreamDestination();
      merger.connect(sinkDest);
      sinkEl = document.createElement('audio');
      sinkEl.autoplay = true; sinkEl.setAttribute('playsinline', ''); sinkEl.muted = false;
      sinkEl.srcObject = sinkDest.stream;
      sinkEl.style.cssText = 'position:fixed;left:-9999px;width:1px;height:1px;opacity:0;pointer-events:none';
      document.body.appendChild(sinkEl);
      try { sinkEl.play(); } catch (e) {}
      applyOutput(); paint(); paintPills();
    } catch (e) {
      // Split unavailable → element stays the audible path (uncentered, no per-bus mute).
      try { if (sinkEl) sinkEl.remove(); } catch (x) {}
      voiceGain = musicGain = sinkDest = null; sinkEl = null;
      applyOutput();
    }
  }
  function hookGraph() {
    audioEl = document.querySelector('#mhv-audio-mount video');
    if (!audioEl || !audioEl.srcObject) { hookT = setTimeout(hookGraph, 250); return; }
    buildGraph(audioEl);
    applyOutput(); paint();
  }
  function teardownGraph() {
    try { if (srcNode) srcNode.disconnect(); } catch (e) {}
    try { if (sinkEl) { sinkEl.pause(); sinkEl.srcObject = null; sinkEl.remove(); } } catch (e) {}
    try { if (ac) ac.close(); } catch (e) {}
    ac = srcNode = splitter = voiceGain = musicGain = merger = sinkDest = null;
    sinkEl = null; audioEl = null;
  }

  // ---- MediaSession (lock-screen / notification controls) ------------------
  function setupMediaSession() {
    if (mediaSessionSet || !('mediaSession' in navigator)) return;
    mediaSessionSet = true;
    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: 'Live Auction Audio', artist: 'Granite Union Auction', album: 'Live'
      });
      navigator.mediaSession.setActionHandler('play', function () { masterOn = true; if (!voiceOn && !musicOn) { voiceOn = true; musicOn = true; } enableAudio(); paint(); paintPills(); });
      navigator.mediaSession.setActionHandler('pause', function () { masterOn = false; applyOutput(); paint(); });
      navigator.mediaSession.setActionHandler('stop', function () { masterOn = false; applyOutput(); paint(); });
    } catch (e) { /* not supported */ }
  }

  // ---- Connect / disconnect ------------------------------------------------
  var prevVideoMute = null;
  function connect() {
    if (connected) return; connected = true;
    ensureUI();
    document.body.classList.add('mhv-audio-mode');
    if (window._mhvPlayer && window._mhvPlayer.getMute) { try { prevVideoMute = window._mhvPlayer.getMute(); } catch (e) { prevVideoMute = null; } }
    if (window._mhvPlayer && window._mhvPlayer.setMute) { try { window._mhvPlayer.setMute(true); } catch (e) {} }
    setupMediaSession();
    startPlayer();
    paint(); paintPills();
  }
  function startPlayer() {
    if (!window.OvenPlayer) { retryT = setTimeout(startPlayer, 500); return; }
    try {
      player = window.OvenPlayer.create('mhv-audio-mount', {
        autoStart: true, mute: true, controls: false,
        sources: [{ type: 'webrtc', file: STREAM }],
        webrtcConfig: { useTCP: true, timeoutMaxRetry: 5, connectionTimeout: 6000 }
      });
      player.on('stateChanged', function (e) { if (e.newstate === 'playing') hookGraph(); });
      player.on('error', function () { if (connected) { try { player.remove(); } catch (x) {} player = null; teardownGraph(); retryT = setTimeout(function () { if (connected) startPlayer(); }, 3000); } });
    } catch (e) { retryT = setTimeout(function () { if (connected) startPlayer(); }, 3000); }
  }
  function disconnect() {
    if (!connected) return; connected = false;
    if (retryT) { clearTimeout(retryT); retryT = null; }
    if (hookT) { clearTimeout(hookT); hookT = null; }
    document.body.classList.remove('mhv-audio-mode');
    closePills();
    teardownGraph();
    try { if (player) player.remove(); } catch (e) {}
    player = null;
    if (window._mhvPlayer && window._mhvPlayer.setMute) { try { window._mhvPlayer.setMute(prevVideoMute === true); } catch (e) {} }
    // Back to video mode — hand the mute icon back to the video player state.
    if (typeof window.updateMuteButtonUI === 'function') { try { window.updateMuteButtonUI(window.mhvMuteState === true); } catch (e) {} }
  }

  function setMode(m) {
    var next = (m === 'audio') ? 'audio' : 'video';
    if (next === mode) return;
    mode = next;
    if (mode === 'audio') connect(); else disconnect();
  }

  // ---- Drop-up caret + Voice/Music pills -----------------------------------
  function closePills() {
    var pop = document.getElementById('mute-bus-popover');
    var caret = document.getElementById('mute-caret-btn');
    if (pop) pop.classList.remove('open');
    if (caret) caret.setAttribute('aria-expanded', 'false');
  }
  function wirePills() {
    if (pillsWired) return;
    var caret = document.getElementById('mute-caret-btn');
    var pop = document.getElementById('mute-bus-popover');
    if (!caret || !pop) return;   // markup not present yet (older cached liquid) — try later
    pillsWired = true;
    caret.addEventListener('click', function (e) {
      e.preventDefault(); e.stopPropagation();
      var open = pop.classList.toggle('open');
      caret.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
    var pv = document.getElementById('mute-pill-voice');
    var pm = document.getElementById('mute-pill-music');
    if (pv) pv.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); toggleBus('voice'); });
    if (pm) pm.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); toggleBus('music'); });
    // Close on outside click / Escape.
    document.addEventListener('click', function (e) {
      if (!pop.classList.contains('open')) return;
      if (e.target === caret || caret.contains(e.target) || pop.contains(e.target)) return;
      closePills();
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closePills(); });
    paintPills();
  }

  // First user gesture unlocks audio (autoplay policy); returning to foreground re-primes it.
  function wireGestures() {
    if (gestureWired) return; gestureWired = true;
    var onG = function () { if (connected) enableAudio(); };
    ['click', 'touchend', 'keydown'].forEach(function (ev) { document.addEventListener(ev, onG, { passive: true }); });
    document.addEventListener('visibilitychange', function () { if (!document.hidden && connected) enableAudio(); });
  }

  function wireSocket() {
    var s = window.socket;
    if (!s || typeof s.on !== 'function') { setTimeout(wireSocket, 700); return; }
    if (s.__mhvAudioWired) return; s.__mhvAudioWired = true;
    s.on('audio-mode-changed', function (d) { setMode(d && d.mode); });
  }

  function init() { ensureUI(); wirePills(); wireGestures(); wireSocket(); if (!pillsWired) setTimeout(wirePills, 1500); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  window.MHVLiveAudio = {
    connect: connect, disconnect: disconnect, toggleMute: toggleMute,
    toggleVoice: function () { toggleBus('voice'); }, toggleMusic: function () { toggleBus('music'); },
    busState: function () { return { master: masterOn, voice: voiceOn, music: musicOn, audible: isAudible() }; },
    get mode() { return mode; }
  };
})();
